'use client';

import { createContext, useContext, type ReactNode, type FC } from 'react';

export interface WeaveAdFormatContextType {
  /** Whether the fallback UI should be rendered (no AdMesh links detected) */
  shouldRenderFallback: boolean;
  /** The message ID for this Weave Ad Format container (used for deduplication) */
  messageId: string;
  /** The session ID for this Weave Ad Format container (used for tracking) */
  sessionId?: string;
  /** The query for this message (used for fallback API calls) */
  query?: string;
}

const WeaveAdFormatContext = createContext<WeaveAdFormatContextType | undefined>(undefined);

export const WeaveAdFormatProvider: FC<{
  children: ReactNode;
  shouldRenderFallback: boolean;
  messageId: string;
  sessionId?: string;
  query?: string;
}> = ({ children, shouldRenderFallback, messageId, sessionId, query }) => {
  return (
    <WeaveAdFormatContext.Provider value={{ shouldRenderFallback, messageId, sessionId, query }}>
      {children}
    </WeaveAdFormatContext.Provider>
  );
};

/**
 * Hook to access WeaveAdFormatContext
 * 
 * Returns the context value if inside a WeaveAdFormatContainer,
 * or undefined if not inside one.
 * 
 * @example
 * ```tsx
 * const weaveContext = useWeaveAdFormatContext();
 * if (weaveContext) {
 *   // Inside a WeaveAdFormatContainer
 *   const { shouldRenderFallback, messageId } = weaveContext;
 * }
 * ```
 */
export const useWeaveAdFormatContext = (): WeaveAdFormatContextType | undefined => {
  return useContext(WeaveAdFormatContext);
};

export default WeaveAdFormatContext;
